import { getImage } from '@fs/zion-sls'

/**
 * Finds the parent event type for a field by recursively searching element hierarchy.
 * This is useful for determining context for fields like "Date" which can belong to
 * different event types (Birth Date, Death Date, etc.)
 *
 * @param {string} fieldId - The ID of the field to find the parent event for
 * @param {string} imageArk - The ARK of the image containing the element structure
 * @param {Object} slsState - The SLS state containing images data
 * @returns {string|null} The parent event type (e.g., "BIRTH", "DEATH") or null if not found
 */
export const findParentEventType = (fieldId, imageArk, slsState) => {
  const image = getImage(slsState?.images, imageArk)
  if (!image?.subElements) return null

  // Recursive function to search through nested subElements
  const searchForField = (elements, currentEvent = null) => {
    if (!Array.isArray(elements)) return null

    return elements.reduce((found, element) => {
      if (found) return found

      // Track event type as we traverse
      const eventType = element.elementType === 'EVENT' ? element.eventType : currentEvent

      // Check if this element is the field we're looking for
      if (element.id === fieldId) {
        return eventType
      }

      // Recursively search subElements
      return element.subElements ? searchForField(element.subElements, eventType) : null
    }, null)
  }

  return searchForField(image.subElements)
}
